import { useState, useEffect } from 'react';
import { TrophyIcon, FireIcon, ChartBarIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { leaderboardService } from '../services';

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('all-time');
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
    fetchMyRank();
  }, [activeTab]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      let data;
      
      if (activeTab === 'all-time') {
        data = await leaderboardService.getAllTime();
      } else if (activeTab === 'weekly') {
        data = await leaderboardService.getWeekly();
      } else {
        data = await leaderboardService.getMonthly();
      }
      
      setLeaderboard(data.rankings || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRank = async () => {
    try {
      const data = await leaderboardService.getMyRank();
      // API returns { rank, stats } - flatten it for easier use
      if (data && data.stats) {
        setMyRank({
          rank: data.rank,
          ...data.stats
        });
      }
    } catch (error) {
      console.error('Error fetching my rank:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank === 2) return 'from-gray-300 to-gray-400';
    if (rank === 3) return 'from-orange-400 to-orange-600';
    return 'from-indigo-500 to-purple-600';
  };

  const tabs = [
    { id: 'all-time', label: 'All-Time', icon: TrophyIcon },
    { id: 'weekly', label: 'This Week', icon: FireIcon },
    { id: 'monthly', label: 'This Month', icon: ChartBarIcon },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <TrophyIcon className="h-12 w-12 text-yellow-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Leaderboard</h1>
          </div>
          <p className="text-lg text-gray-600">
            Compete with fellow learners and climb the ranks!
          </p>
        </div>

        {/* My Rank Card */}
        {myRank && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 rounded-full p-3">
                  <UserCircleIcon className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-sm font-medium opacity-90">Your Current Rank</p>
                  <p className="text-3xl font-bold">#{myRank.rank}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90">Total Score</p>
                <p className="text-2xl font-bold">{myRank.score.toLocaleString()}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/20">
              <div className="text-center">
                <p className="text-sm opacity-90">Badge Points</p>
                <p className="text-xl font-semibold">{myRank.badge_points}</p>
              </div>
              <div className="text-center">
                <p className="text-sm opacity-90">Completion</p>
                <p className="text-xl font-semibold">{myRank.completion_percentage}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm opacity-90">Speed Bonus</p>
                <p className="text-xl font-semibold">{myRank.speed_bonus}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="flex border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="mb-8">
                <div className="flex items-end justify-center space-x-4 mb-8">
                  {/* 2nd Place */}
                  <div className="flex-1 max-w-xs">
                    <div className="bg-gradient-to-br from-gray-300 to-gray-400 rounded-t-xl p-6 text-center text-white shadow-lg">
                      <div className="text-4xl mb-2">🥈</div>
                      <p className="text-lg font-semibold truncate">{leaderboard[1].name}</p>
                      <p className="text-2xl font-bold mt-2">{leaderboard[1].score.toLocaleString()}</p>
                      <p className="text-sm opacity-90 mt-1">points</p>
                    </div>
                    <div className="bg-gray-200 h-24 rounded-b-lg"></div>
                  </div>

                  {/* 1st Place */}
                  <div className="flex-1 max-w-xs">
                    <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-t-xl p-6 text-center text-white shadow-xl transform scale-105">
                      <div className="text-5xl mb-2 animate-bounce">🥇</div>
                      <SparklesIcon className="h-6 w-6 mx-auto mb-2" />
                      <p className="text-xl font-bold truncate">{leaderboard[0].name}</p>
                      <p className="text-3xl font-bold mt-2">{leaderboard[0].score.toLocaleString()}</p>
                      <p className="text-sm opacity-90 mt-1">points</p>
                    </div>
                    <div className="bg-yellow-300 h-32 rounded-b-lg"></div>
                  </div>

                  {/* 3rd Place */}
                  <div className="flex-1 max-w-xs">
                    <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-t-xl p-6 text-center text-white shadow-lg">
                      <div className="text-4xl mb-2">🥉</div>
                      <p className="text-lg font-semibold truncate">{leaderboard[2].name}</p>
                      <p className="text-2xl font-bold mt-2">{leaderboard[2].score.toLocaleString()}</p>
                      <p className="text-sm opacity-90 mt-1">points</p>
                    </div>
                    <div className="bg-orange-200 h-16 rounded-b-lg"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Rankings List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Full Rankings</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {leaderboard.map((user, index) => (
                  <div
                    key={user.rank}
                    className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                      user.is_current_user ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        {/* Rank */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br ${getRankColor(user.rank)} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                          {getRankIcon(user.rank) || `#${user.rank}`}
                        </div>

                        {/* Name & Stats */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-lg font-semibold text-gray-900 truncate">
                              {user.name}
                            </p>
                            {user.is_current_user && (
                              <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                                You
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                            <span className="flex items-center">
                              <TrophyIcon className="h-4 w-4 text-yellow-500 mr-1" />
                              {user.badge_points} pts
                            </span>
                            <span>{user.completion_percentage}% complete</span>
                            <span className="hidden sm:inline">
                              {user.completed_modules}M • {user.completed_labs}L • {user.completed_capstones}C
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Score */}
                      <div className="flex-shrink-0 text-right ml-4">
                        <p className="text-2xl font-bold text-gray-900">
                          {user.score.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">total points</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Empty State */}
            {leaderboard.length === 0 && (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <TrophyIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Rankings Yet</h3>
                <p className="text-gray-600">
                  Complete modules and labs to appear on the leaderboard!
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
